import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, differenceInDays } from "date-fns";
import { Eye, AlertTriangle, CheckCircle2, Clock, Shield, FileText, ChevronRight } from "lucide-react";
import { RequirePermission } from "@/components/RequirePermission";
import { useIsMobile } from "@/hooks/use-mobile";

interface ComplianceVendor {
  id: string;
  name: string;
  company_id: string;
  onboarding_track: string;
  onboarding_status: string;
  tax_locked: boolean | null;
  w9_uploaded: string | null;
  agreement_signed: string | null;
  agreement_date: string | null;
  gl_id: string | null;
  gl_expiry: string | null;
  gl_verified: string | null;
  wc_id: string | null;
  wc_expiry: string | null;
  wc_exemption_type: string | null;
  wc_verified: string | null;
  license_id: string | null;
  license_expiry: string | null;
  license_verified: string | null;
  next_expiry_date: string | null;
  computed_compliance_status: string;
}

interface I9Summary { vendor_id: string; i9_status: string; }
interface EVerifySummary { vendor_id: string; case_status: string; }

const STATUS_CONFIG: Record<string, { emoji: string; label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  approved: { emoji: "🟢", label: "Approved", variant: "default" },
  pending: { emoji: "🟡", label: "Pending", variant: "secondary" },
  under_review: { emoji: "🔵", label: "Under Review", variant: "secondary" },
  incomplete: { emoji: "🔴", label: "Incomplete", variant: "destructive" },
  expired: { emoji: "🔴", label: "Expired", variant: "destructive" },
  expiring_soon: { emoji: "🟠", label: "Expiring Soon", variant: "secondary" },
  not_invited: { emoji: "⚫", label: "Not Invited", variant: "outline" },
};

const ComplianceInner = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("all");

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ["vendor_compliance_status"],
    queryFn: async () => {
      const { data, error } = await (supabase.from("vendor_compliance_status") as any).select("*").order("name");
      if (error) throw error;
      return (data ?? []) as ComplianceVendor[];
    },
    enabled: !!user,
  });

  const { data: i9Records = [] } = useQuery({
    queryKey: ["all_i9_records"],
    queryFn: async () => {
      const { data, error } = await (supabase.from("worker_i9_records") as any).select("vendor_id, i9_status");
      if (error) throw error;
      return (data ?? []) as I9Summary[];
    },
    enabled: !!user,
  });

  const { data: everifyCases = [] } = useQuery({
    queryKey: ["all_everify_cases"],
    queryFn: async () => {
      const { data, error } = await (supabase.from("worker_everify_cases") as any)
        .select("vendor_id, case_status")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as EVerifySummary[];
    },
    enabled: !!user,
  });

  const i9Map = useMemo(() => {
    const map: Record<string, string> = {};
    i9Records.forEach(r => { if (!map[r.vendor_id]) map[r.vendor_id] = r.i9_status; });
    return map;
  }, [i9Records]);

  const everifyMap = useMemo(() => {
    const map: Record<string, string> = {};
    everifyCases.forEach(r => { if (!map[r.vendor_id]) map[r.vendor_id] = r.case_status; });
    return map;
  }, [everifyCases]);

  const contractLabor = useMemo(() => vendors.filter(v => v.onboarding_track === "personnel"), [vendors]);
  const pendingReview = useMemo(() => vendors.filter(v => v.computed_compliance_status === "under_review" || v.onboarding_status === "submitted"), [vendors]);
  const expiringSoon = useMemo(() => vendors.filter(v => v.computed_compliance_status === "expiring_soon" || v.computed_compliance_status === "expired"), [vendors]);
  const incomplete = useMemo(() => vendors.filter(v => v.computed_compliance_status === "incomplete"), [vendors]);
  const approved = useMemo(() => vendors.filter(v => v.computed_compliance_status === "approved"), [vendors]);

  const renderStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return <Badge variant={config.variant} className="text-[10px]">{config.emoji} {config.label}</Badge>;
  };

  const renderCheckIcon = (ok: boolean) => (
    <span className={ok ? "text-primary" : "text-destructive"}>{ok ? "✅" : "❌"}</span>
  );

  const renderExpiryBadge = (date: string | null) => {
    if (!date) return <span className="text-xs text-muted-foreground">—</span>;
    const days = differenceInDays(new Date(date), new Date());
    if (days < 0) return <Badge variant="destructive" className="text-[10px]">Expired {Math.abs(days)}d ago</Badge>;
    if (days < 30) return <Badge variant="secondary" className="text-[10px]">⚠️ {days}d left</Badge>;
    return <span className="text-xs text-muted-foreground">{format(new Date(date), "MMM d, yyyy")}</span>;
  };

  // Mobile card for vendor compliance
  const renderVendorCard = (v: ComplianceVendor) => (
    <Card key={v.id} className="cursor-pointer active:scale-[0.98] transition-transform" onClick={() => navigate(`/controller/vendors/${v.id}`)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{v.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px]">
                {v.onboarding_track === "personnel" ? "🧑 Personnel" : "👷 Sub"}
              </Badge>
              {renderStatusBadge(v.computed_compliance_status)}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
          <span>{renderCheckIcon(!!v.tax_locked)} Tax/W9</span>
          <span>{renderCheckIcon(!!v.agreement_signed)} Agreement</span>
          {v.onboarding_track === "subcontractor" ? (
            <span>{renderCheckIcon(!!v.gl_id)} Insurance</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
        {v.next_expiry_date && (
          <div className="mt-2">{renderExpiryBadge(v.next_expiry_date)}</div>
        )}
      </CardContent>
    </Card>
  );

  // Mobile card for contract labor
  const renderContractLaborCard = (v: ComplianceVendor) => {
    const i9Status = i9Map[v.id];
    const evStatus = everifyMap[v.id];
    return (
      <Card key={v.id} className="cursor-pointer active:scale-[0.98] transition-transform" onClick={() => navigate(`/controller/vendors/${v.id}`)}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <p className="font-medium truncate">{v.name}</p>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1 text-[10px]">
            <span>{renderCheckIcon(i9Status === "complete")} I-9: {i9Status === "complete" ? "Done" : i9Status || "None"}</span>
            <span>{renderCheckIcon(evStatus === "authorized")} E-Verify: {evStatus === "authorized" ? "Auth" : evStatus || "N/A"}</span>
            <span>{renderCheckIcon(!!v.tax_locked)} W9</span>
            <span>{renderCheckIcon(!!v.agreement_signed)} Agreement</span>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderVendorRow = (v: ComplianceVendor) => (
    <TableRow key={v.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/controller/vendors/${v.id}`)}>
      <TableCell className="font-medium">{v.name}</TableCell>
      <TableCell>
        <Badge variant="outline" className="text-[10px]">
          {v.onboarding_track === "personnel" ? "🧑 Personnel" : "👷 Sub"}
        </Badge>
      </TableCell>
      <TableCell>{renderStatusBadge(v.computed_compliance_status)}</TableCell>
      <TableCell>
        <div className="flex flex-col gap-0.5 text-[10px]">
          <span>{renderCheckIcon(!!v.tax_locked)} Tax/W9</span>
          <span>{renderCheckIcon(!!v.agreement_signed)} IC Agreement</span>
          {v.onboarding_track === "subcontractor" && (
            <>
              <span>{renderCheckIcon(!!v.gl_id)} GL Insurance</span>
              <span>{renderCheckIcon(!!v.wc_id || !!v.wc_exemption_type)} Workers Comp</span>
              <span>{renderCheckIcon(!!v.license_id)} License</span>
            </>
          )}
        </div>
      </TableCell>
      <TableCell>{renderExpiryBadge(v.next_expiry_date)}</TableCell>
      <TableCell>
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/controller/vendors/${v.id}`); }}>
          <Eye className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );

  const renderContractLaborRow = (v: ComplianceVendor) => {
    const i9Status = i9Map[v.id];
    const evStatus = everifyMap[v.id];
    return (
      <TableRow key={v.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/controller/vendors/${v.id}`)}>
        <TableCell className="font-medium">{v.name}</TableCell>
        <TableCell>
          <span className="text-[10px]">{renderCheckIcon(i9Status === "complete")} {i9Status === "complete" ? "Done" : i9Status ? "Pending" : "None"}</span>
        </TableCell>
        <TableCell>
          <span className="text-[10px]">{renderCheckIcon(evStatus === "authorized")} {evStatus === "authorized" ? "Auth" : evStatus || "Not sub"}</span>
        </TableCell>
        <TableCell><span className="text-[10px]">{renderCheckIcon(!!v.tax_locked)}</span></TableCell>
        <TableCell><span className="text-[10px]">{renderCheckIcon(!!v.agreement_signed)}</span></TableCell>
        <TableCell>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/controller/vendors/${v.id}`); }}>
            <Eye className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  const renderTable = (list: ComplianceVendor[]) => isMobile ? (
    <div className="space-y-3">
      {list.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No vendors in this category</p>
      ) : list.map(renderVendorCard)}
    </div>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Vendor</TableHead><TableHead>Track</TableHead><TableHead>Status</TableHead>
          <TableHead>Checklist</TableHead><TableHead>Next Expiry</TableHead><TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.length === 0 ? (
          <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No vendors in this category</TableCell></TableRow>
        ) : list.map(renderVendorRow)}
      </TableBody>
    </Table>
  );

  const renderContractLaborContent = () => isMobile ? (
    <div className="space-y-3">
      {contractLabor.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No contract labor vendors</p>
      ) : contractLabor.map(renderContractLaborCard)}
    </div>
  ) : (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Worker</TableHead><TableHead>I-9</TableHead><TableHead>E-Verify</TableHead>
          <TableHead>W9</TableHead><TableHead>Agreement</TableHead><TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contractLabor.length === 0 ? (
          <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No contract labor vendors</TableCell></TableRow>
        ) : contractLabor.map(renderContractLaborRow)}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Compliance Review</h1>
          <p className="text-sm text-muted-foreground">Vendor onboarding, I-9, E-Verify, and compliance document tracking</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab("contract_labor")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Contract Labor</p>
            </div>
            <p className="text-2xl font-bold">{contractLabor.length}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab("pending")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-warning" />
              <p className="text-xs text-muted-foreground">Pending Review</p>
            </div>
            <p className="text-2xl font-bold">{pendingReview.length}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab("expiring")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <p className="text-xs text-muted-foreground">Expiring / Expired</p>
            </div>
            <p className="text-2xl font-bold">{expiringSoon.length}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab("incomplete")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-warning" />
              <p className="text-xs text-muted-foreground">Incomplete</p>
            </div>
            <p className="text-2xl font-bold">{incomplete.length}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab("approved")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Approved</p>
            </div>
            <p className="text-2xl font-bold">{approved.length}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={isMobile ? "w-full overflow-x-auto flex justify-start" : "flex-wrap"}>
          <TabsTrigger value="contract_labor" className="shrink-0">Contract Labor ({contractLabor.length})</TabsTrigger>
          <TabsTrigger value="pending" className="shrink-0">Pending ({pendingReview.length})</TabsTrigger>
          <TabsTrigger value="expiring" className="shrink-0">Expiring ({expiringSoon.length})</TabsTrigger>
          <TabsTrigger value="incomplete" className="shrink-0">Incomplete ({incomplete.length})</TabsTrigger>
          <TabsTrigger value="approved" className="shrink-0">Approved ({approved.length})</TabsTrigger>
          <TabsTrigger value="all" className="shrink-0">All ({vendors.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="contract_labor" className="pt-2">{renderContractLaborContent()}</TabsContent>
        <TabsContent value="pending" className="pt-2">{renderTable(pendingReview)}</TabsContent>
        <TabsContent value="expiring" className="pt-2">{renderTable(expiringSoon)}</TabsContent>
        <TabsContent value="incomplete" className="pt-2">{renderTable(incomplete)}</TabsContent>
        <TabsContent value="approved" className="pt-2">{renderTable(approved)}</TabsContent>
        <TabsContent value="all" className="pt-2">{renderTable(vendors)}</TabsContent>
      </Tabs>
    </div>
  );
};

const Compliance = () => (
  <RequirePermission anyOf={["manage_vendors", "view_compliance"]}>
    <ComplianceInner />
  </RequirePermission>
);

export default Compliance;